import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { DEMOS, usePlay } from "~/common/auth/panel-demos";
import { useT } from "~/core/i18n";
import { LOOP } from "~/modules/analytics/lib/loop";
import { Logo } from "~/shell/logo";

/** How long one stage holds the stage before the loop moves on. */
const DWELL = 5200;

/**
 * The column beside the form — on sign-in, on a magic link, and on every screen
 * of the wizard.
 *
 * It was five headings and five sentences: a table of contents for a product
 * nobody had seen yet. This is the second-highest-traffic surface the product
 * has and the last one somebody reads before deciding whether to type an
 * address, so it demonstrates the loop instead of listing it — one stage at a
 * time, each showing the mechanism a stage runs on.
 *
 * The stage list stays, compressed to a row each: it is the map, and the
 * demonstration is what is happening at the pin. `LOOP` is still the table both
 * read, so a stage added there arrives here with its number and its name.
 *
 * The wizard pins the stage to the step being answered rather than cycling —
 * somebody filling in a field is not watching a carousel, and the panel's job
 * there is to say what that particular answer buys.
 */
export function AuthPanel({ headline, note, stage }: { headline?: string; note?: string; stage?: string }) {
	const t = useT();
	const play = usePlay();
	const pinned = stage ? LOOP.findIndex((entry) => entry.key === stage) : -1;
	const [at, setAt] = useState(0);
	/** A click is a decision to read one stage; the cycle does not take it back. */
	const [held, setHeld] = useState(false);
	const index = pinned >= 0 ? pinned : at;
	const current = LOOP[index];
	const Demo = DEMOS[current.key];
	const cycling = pinned < 0 && !held && play;

	useEffect(() => {
		if (!cycling) return;
		const timer = setInterval(() => setAt((previous) => (previous + 1) % LOOP.length), DWELL);
		return () => clearInterval(timer);
	}, [cycling]);

	return (
		<aside className="auth-context" data-stage={current.key}>
			<Decoration />
			<Logo size="md" />
			<div className="auth-lede">
				<h2>{headline ?? t("login.headline")}</h2>
				<p>{note ?? t("login.pitch")}</p>
			</div>

			<div className="auth-stage">
				<span className="auth-sample">{t("auth.demo.sample")}</span>
				<AnimatePresence mode="wait" initial={false}>
					<motion.div
						key={current.key}
						className="auth-stage-body"
						initial={play ? { opacity: 0, y: 10 } : false}
						animate={{ opacity: 1, y: 0 }}
						exit={play ? { opacity: 0, y: -10 } : undefined}
						transition={{ duration: 0.28, ease: "easeOut" }}
					>
						<h3>{t(`nav.stage.${current.key}`)}</h3>
						{Demo ? <Demo play={play} /> : null}
					</motion.div>
				</AnimatePresence>
			</div>

			<ol className="auth-loop">
				{LOOP.map((entry, position) => {
					const name = t(`nav.stage.${entry.key}`);
					const inner = (
						<>
							<span className="auth-step num" aria-hidden>
								{entry.step}
							</span>
							<span className="auth-step-name">{name}</span>
						</>
					);
					return (
						<li key={entry.key} data-current={position === index || undefined}>
							{pinned >= 0 ? (
								<span className="auth-step-row">{inner}</span>
							) : (
								<button
									type="button"
									className="auth-step-row"
									aria-current={position === index ? "step" : undefined}
									onClick={() => {
										setHeld(true);
										setAt(position);
									}}
								>
									{inner}
								</button>
							)}
							{position === index && cycling ? (
								<motion.span
									className="auth-step-progress"
									key={`${entry.key}-progress`}
									initial={{ scaleX: 0 }}
									animate={{ scaleX: 1 }}
									transition={{ duration: DWELL / 1000, ease: "linear" }}
								/>
							) : null}
						</li>
					);
				})}
			</ol>

			<p className="auth-engines">{t("auth.engines")}</p>
		</aside>
	);
}

/**
 * The ground the column stands on.
 *
 * Three soft fields of colour and three outlines, drifting slowly, with the
 * hue set by the stage on screen — the panel's only ornament, and the reason
 * it reads as a place rather than a form's left margin. It is decoration in
 * the strict sense: nothing here encodes anything, so a reader who cannot see
 * the colours loses nothing, and CSS holds all of it so no frame of it runs
 * through React.
 */
function Decoration() {
	return (
		<div className="auth-decor" aria-hidden>
			<span className="auth-blob" data-blob="1" />
			<span className="auth-blob" data-blob="2" />
			<span className="auth-blob" data-blob="3" />
			<svg className="auth-geo" viewBox="0 0 200 200" fill="none" stroke="currentColor" aria-hidden>
				<polygon points="100,6 181,53 181,147 100,194 19,147 19,53" data-geo="1" />
				<circle cx="100" cy="100" r="58" data-geo="2" />
				<rect x="58" y="58" width="84" height="84" data-geo="3" />
			</svg>
		</div>
	);
}
